import { useSlotT } from '@/cms/useSlotT'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/ui'
import SEO from '@/utils/seo'
import { buildBreadcrumbList } from '@/utils/structuredData'
import ProductOffersSection from '@/components/products/ProductOffersSection'
import CtaBanner from '@/components/home/CtaBanner'

export default function ProductsPage() {
  const slotT = useSlotT('products')
  const { t } = useTranslation()
  return (
    <>
      <SEO
        title={slotT('seo.productsTitle')}
        description={slotT('seo.productsDescription')}
        keywords={slotT('seo.productsKeywords')}
        jsonLd={[
          buildBreadcrumbList([
            { name: t('nav.home'), path: '/' },
            { name: t('nav.products'), path: '/products' },
          ]),
          // No FAQPage schema here any more. The FAQ section was removed
          // from the page on 2026-11-04 (see the note further down), and
          // FAQPage markup for questions that are no longer on the page is
          // structured data that does not describe the page — Google
          // reserves the rich result for pages that show the questions
          // visibly, and a schema that outruns the content is the kind of
          // thing that earns a manual action. The builder itself stays in
          // the codebase: ProductDetailPage still emits it for the Q&A
          // blocks it actually renders.
        ]}
      />
      {/* No `tag`: the tagline and the <h1> would print the same two words
          (「服務與產品」), one above the other.
          2026-11-04: the hero title is painted again — the page needs a
          visible <h1> that says what the page is. */}
      <PageHeader
        title={slotT('products.hero.title')}
        description={slotT('products.hero.description')}
      />

      {/*
        一、產品與服務 — the product catalogue.

        The section lives in its own file
        (`@/components/products/ProductOffersSection`) so the page's own
        markup stays readable; it renders unchanged. Its copy
        (`products.offers.*`, `products.cta`) comes from the i18n files and
        the CMS slots.

        It opens the page's body, directly under the page header: the
        catalogue is what the page is for, so the products come first and
        the organisation's story lives on /about.
      */}
      <ProductOffersSection />

      {/*
        The three blocks that used to sit here — 「我們如何與學校同行」
        (the 導入前 / 導入中 / 導入後 band), 「四大服務範疇」 and
        「我們也是教育者」 — moved to /about on 2026-11-04.

        They describe the ORGANISATION, not the catalogue: who we are, what
        four areas we work in, and how we work with a school from the first
        demo to the year-after-year support. On this page they interrupted
        the product list; on /about they are the substance of the page.

        Their copy moved with them (`about.process.*`, `about.areas.title`,
        `about.educators.*`, `about.servicesIntro`), and the i18n keys they
        used on this page (`products.areas.*`, `products.intro`,
        `products.educators.*`) are gone from the locale files.
      */}

      {/*
        二、方案與定價 and 三、我們如何支援學校？ — removed 2026-11-04.

        Both sections are gone from the page at the owner's request. They
        are not hidden with CSS: display:none content still ships in the
        HTML, still gets indexed by crawlers and still sits in the
        document for anyone reading the source. The markup is deleted
        instead, and their copy survives in the i18n files
        (`products.plans.*` / `products.support.*`) so the sections can
        be restored without rewriting them. Nothing links to
        `#plans-title` or `#support-title`, so no anchor breaks.

        The pricing band (三) was the page's only 量身訂製 statement and
        the only inline WhatsApp / contact CTAs — the closing 定制化支援服務
        section below still carries both, so the page keeps a way to enquire.
      */}

      {/*
        四、常見問題 — removed 2026-11-04.

        The FAQ section is gone from the page at the owner's request. Like
        the two sections above it, it is not hidden with CSS:
        display:none content still ships in the HTML, still gets indexed by
        crawlers and still sits in the document for anyone reading the
        source. The markup is deleted instead, and its copy survives in the
        i18n files (`products.faq.items`) plus the CMS slot definitions
        (`products.faq.tag` / `products.faq.title`) so the section can be
        restored without rewriting it. Nothing links to `#faq-title`, so no
        anchor breaks.

        The FAQPage JSON-LD that used to accompany it is removed with it —
        see the note on the <SEO> block above. Structured data for content
        that is not on the page is a lie to a crawler, and Google's own
        guidance is that FAQ rich results are shown only for pages that
        carry the questions visibly.

        The five Q&A pairs are not lost: they are the same questions the
        定制化支援服務 band answers in prose, and the closing CTA banner
        still gives a school a direct route to a person.
      */}

      {/*
        行動呼籲 — the CtaBanner that used to sit in the middle of the
        homepage, moved here 2026-11-04.

        It replaces this page's own closing 定制化支援服務 band, which did
        the same job (WhatsApp + a way to enquire) with almost the same
        copy; keeping both would have put two identical calls to action on
        one page. The band's copy survives in the i18n files
        (`products.custom.*`) so it can be restored without rewriting it,
        and `products.custom.whatsapp` / `products.custom.form` are still
        in use on the product detail pages.
      */}
      <CtaBanner />
    </>
  )
}